import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send } from "lucide-react";
import { EASE, Label } from "./SlideKit";

const Wave = ({ active }) => (
  <div className="flex items-center gap-1 h-10">
    {[0, 1, 2, 3, 4, 5, 6].map((i) => (
      <div key={i} className={`wave-bar h-full ${active ? "" : "idle"}`} style={{ animationDelay: `${i * 0.11}s` }} />
    ))}
  </div>
);

export const VoiceOverlay = ({ phase, liveQuestion, interim, answerText, onCancel, onTypedQuestion, onYes, onNo }) => {
  const [typed, setTyped] = useState("");
  const visible = ["listening", "thinking", "answering", "confirming", "awaiting_continue"].includes(phase);

  const submit = () => {
    if (typed.trim()) {
      onTypedQuestion(typed.trim());
      setTyped("");
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: EASE }}
          className="fixed inset-0 z-40 flex items-end sm:items-center justify-center bg-black/75 backdrop-blur-2xl"
          data-testid="voice-overlay"
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="w-full max-w-2xl border border-neutral-800 bg-[#0a0a0a] p-8 sm:p-10 m-4"
          >
            <div className="flex items-center justify-between mb-6">
              <Label className="text-[#dd6611]">
                {phase === "listening" && "Xena is listening"}
                {phase === "thinking" && "Consulting the dossier"}
                {phase === "answering" && "Xena — X Scale Concierge"}
                {phase === "confirming" && "Did that answer your question?"}
                {phase === "awaiting_continue" && "Shall I continue the presentation?"}
              </Label>
              <button data-testid="overlay-close-btn" onClick={onCancel} className="text-neutral-500 hover:text-white transition-colors duration-200">
                <X size={16} strokeWidth={1.5} />
              </button>
            </div>

            <div className="flex items-center gap-6 mb-6">
              <Wave active={phase === "listening" || phase === "answering"} />
              {phase === "thinking" && (
                <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-neutral-400 pulse-dot">Searching the strategy…</span>
              )}
            </div>

            {(phase === "listening") && (
              <>
                <div className="min-h-[48px] text-lg font-light text-white leading-relaxed" data-testid="live-transcript">
                  {liveQuestion}
                  <span className="text-neutral-500">{interim ? ` ${interim}` : ""}</span>
                  {!liveQuestion && !interim && <span className="text-neutral-600">Speak your question…</span>}
                </div>
                <div className="mt-6 flex gap-2 border-t border-neutral-800 pt-5">
                  <input
                    data-testid="question-input"
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && submit()}
                    placeholder="…or type your question"
                    className="flex-1 min-w-0 bg-transparent border border-neutral-800 px-4 py-3 text-base sm:text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-[#dd6611] transition-colors duration-200"
                  />
                  <button data-testid="question-submit-btn" onClick={submit}
                    className="border border-neutral-700 px-4 text-neutral-300 hover:border-[#dd6611] hover:text-white transition-colors duration-200">
                    <Send size={15} strokeWidth={1.5} />
                  </button>
                </div>
              </>
            )}

            {(phase === "answering" || phase === "confirming" || phase === "awaiting_continue") && answerText && (
              <div className="max-h-56 overflow-y-auto text-base font-light text-neutral-300 leading-relaxed pr-2" data-testid="answer-text">
                {answerText}
              </div>
            )}

            {(phase === "confirming" || phase === "awaiting_continue") && (
              <div className="mt-8 flex gap-3 border-t border-neutral-800 pt-6">
                <button data-testid="confirm-yes-btn" onClick={onYes}
                  className="border border-[#dd6611] px-8 py-2.5 font-mono text-[11px] tracking-[0.2em] uppercase text-white hover:bg-[#dd6611] hover:text-black transition-colors duration-200">
                  Yes
                </button>
                <button data-testid="confirm-no-btn" onClick={onNo}
                  className="border border-neutral-700 px-8 py-2.5 font-mono text-[11px] tracking-[0.2em] uppercase text-neutral-300 hover:border-white hover:text-white transition-colors duration-200">
                  {phase === "confirming" ? "Not quite" : "Ask another"}
                </button>
                <span className="self-center font-mono text-[9px] tracking-[0.15em] uppercase text-neutral-600 ml-2 hidden sm:block">
                  or just say it
                </span>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
